import { CopyField } from '@eb/eds-copy-field';
import { IconButton } from '@eb/eds-icon-button';
import { ShareIosChunky } from '@eb/eds-iconography';
import { Modal } from '@eb/eds-modal';
import { withTooltip } from '@eb/eds-tooltip';
import { gettext } from '@eb/i18n';
import { keysCamelToSnake } from '@eb/transformation-utils';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { formatUrl } from 'url-lib';
import { useDialog } from '../../hooks/useDialog';
import '../../styles/button.scss';
import { trackShareAction } from '../../tracking/trackShareAction';
import { TrackedShareEvent } from '../ShareEvent/TrackedShareEvent';

export interface ShareEventModalProps {
    eventId: string;
    eventName: string;
    eventUrl: string;
    facebookOptions: {
        appId: string;
        locale: string;
        page: string;
        version: string;
        xfbml: boolean;
    };
    isMobile: boolean;
    serverUrl: string;
    shareTypes?: string[];
    twitterHandle: string;
    utmOptions?: {
        'utm-campaign': string;
        'utm-content': string;
        'utm-medium': string;
        'utm-source'?: string;
        'utm-term': string;
    };
    emailOptions?: {
        bodyCopy: string;
        subjectCopy: string;
    };
}

const _getEventUrl = (eventUrl: string, utmOptions: any): string =>
    formatUrl(
        eventUrl,
        keysCamelToSnake({
            ...utmOptions,
            'utm-source': 'cp',
            aff: 'escb',
        }),
    );

const ShareEventModal = (props: ShareEventModalProps) => {
    const { openDialog, closeDialog, isDialogOpen } = useDialog();
    const { eventUrl, utmOptions } = props;
    const openModal = (e: React.MouseEvent) => {
        openDialog(e);
        trackShareAction('open-modal');
    };
    const modalWrapperId = 'share-event-modal-wrapper';

    useEffect(() => {
        const modalWrapper = document.createElement('div');
        modalWrapper.setAttribute('id', modalWrapperId);
        document.body.appendChild(modalWrapper);
    }, []);

    const ShareModal = () => {
        const domElement = document.getElementById(modalWrapperId);

        if (isDialogOpen) {
            return ReactDOM.createPortal(
                <Modal
                    title={gettext('Share with friends')}
                    onClose={closeDialog}
                    isShown={isDialogOpen}
                    noMinHeight={true}
                    roleIsDialog={true}
                >
                    <section className="eds-align--center eds-l-pad-vert-8">
                        <div className="eds-g-cell-8-12">
                            <div className="eds-align--center eds-l-pad-bot-8">
                                <TrackedShareEvent {...props} />
                            </div>
                            <CopyField
                                inputFieldId="eventUrlField"
                                label={gettext('Event URL')}
                                value={_getEventUrl(eventUrl, utmOptions)}
                            />
                        </div>
                    </section>
                </Modal>,
                domElement as HTMLInputElement,
            );
        }
        return null;
    };

    const IconWithTooltip = withTooltip(IconButton);
    return (
        <>
            <IconWithTooltip
                iconType={<ShareIosChunky />}
                onClick={openModal}
                title={gettext('Share this event')}
                tooltipId="tooltip-share-button"
                tooltipText={gettext('Share Event')}
            />
            <ShareModal />
        </>
    );
};

export { ShareEventModal as default };
